import React from "react";
import Typography from "@mui/material/Typography";
import styled from "@emotion/styled";

import Product from "./Product";

const AsanaInZendesk = () => {
  const productName = "Asana in Zendesk";
  const productLogoUrl = "https://d2sztxeooqpvky.cloudfront.net/asana.png";

  const Content = () => {
    return (
      <Styled>
        <Typography variant="h3" gutterBottom id="top-of-page">
          Contents
        </Typography>
        <ul>
          <Typography variant="body1">
            <li>
              <a href="#installation">Installation</a>
            </li>
            <li>
              <a href="#usage">Usage</a>
            </li>
          </Typography>
          <ul>
            <Typography variant="body1">
              <li>
                <a href="#create-asana-task">Create a new Asana task</a>
              </li>
              <li>
                <a href="#link-asana-task"> Link an existing Asana task</a>
              </li>
              <li>
                <a href="#unlink-asana-task">Unlinking a task</a>
              </li>
              <li>
                <a href="#comment-on-asana-task">Commenting on a task</a>
              </li>
              <li>
                <a href="#changing-asana-user">Changing Asana user</a>
              </li>
              <li id="installation">
                <a href="#changing-zendesk-user">Changing Zendesk user</a>
              </li>
            </Typography>
          </ul>
          <Typography variant="body1">
            <li>
              <a href="#pricing">Pricing</a>
            </li>
          </Typography>
        </ul>
        <Typography variant="h3" gutterBottom>
          Installation
        </Typography>
        <Typography variant="body1" gutterBottom>
          1. Visit the{" "}
          <a
            href="https://www.zendesk.com/marketplace/apps/support/1131851/asana-by-thackd/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Zendesk Marketplace
          </a>{" "}
          and install the 'Asana in Zendesk' app
        </Typography>
        <Typography variant="body1" gutterBottom>
          As part of the install flow you will be asked to authorize the app to
          access your Zendesk and Asana accounts. We only ask for permissions
          strictly needed for the app functionality.{" "}
        </Typography>
        <div className="border-left">
          <Typography variant="body1" gutterBottom>
            ℹ️ We recommend creating a dedicated Asana user and Zendesk user for
            the app and install it using this user. This will give various
            benefits, including:
          </Typography>
          <ul>
            <Typography variant="body1">
              <li>
                Asana tasks created via the app will have a generic author.
              </li>
              <li>You can give the appropriate permissions to these users.</li>
              <li>
                Notes posted in Zendesk will have a generic author and will
                appear as new notes for you
              </li>
            </Typography>
          </ul>
        </div>
        <Typography variant="body1" gutterBottom>
          2. First you will be asked to authorize the app’s access to your
          Zendesk account:
        </Typography>
        <Typography variant="body1" gutterBottom>
          3. After clicking “Authorize” you will be redirected to Asana and
          asked to grant permission for “Asana in Zendesk (thackd.com)” to
          access your Asana account:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/asana/asana_permissions.png"
          alt="Asana authorization"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          4. After clicking “Allow” you will be redirected to the Zendesk
          Marketplace and shown instructions on how to use Asana in Zendesk.
        </Typography>
        <Typography variant="body1" gutterBottom>
          5. Go back to Zendesk Support and while looking at a ticket, open the
          apps sidebar on the right and locate Asana by Thackd:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-in-the-sidebar.png"
          alt="Asana in the Zendesk sidebar"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          If you have any issues with the above, please contact us at:{" "}
          <a href="mailto:support@thackd.com">support@thackd.com</a>
        </Typography>
        <Typography variant="body1" gutterBottom id="usage">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h3" gutterBottom>
          Usage
        </Typography>

        <Typography variant="body1">
          The app gives you 2 main options when viewing a ticket in Zendesk:
        </Typography>
        <ol>
          <Typography variant="body1">
            <li>
              <a href="#create-asana-task">Create a new Asana task</a>
            </li>
            <li>
              <a href="#link-asana-task"> Link an existing Asana task</a>
            </li>
          </Typography>
        </ol>
        <Typography variant="body1">
          You can also <a href="#unlink-asana-task">unlink tasks</a> and{" "}
          <a href="#comment-on-asana-task">comment on tasks</a> that you have
          created or linked from the ticket.
        </Typography>
        <Typography variant="body1" gutterBottom id="create-asana-task">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h4" gutterBottom>
          1. Create a new Asana task
        </Typography>
        <Typography variant="body1" gutterBottom>
          This will create a new task on the specified project in Asana and link
          the Zendesk ticket and task. It will also post a link to the task in a
          note on the Zendesk ticket and a link to the Zendesk ticket on the
          Asana task.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Additionally, any notes posted on the Asana task or changes in its
          status will be posted as notes on the Zendesk ticket.
        </Typography>

        <Typography variant="body1" gutterBottom>
          1. Select “Create a new task”:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-home-empty.png"
          alt="Asana in the sidebar"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          2. Search for the Asana project you wish to create the new task in by
          entering the full or partial project name into the text box and
          clicking the button:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-search-project.png"
          alt="Search project"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          3. Select the project from the list of results:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-select-project.png"
          alt="Select project"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          4. Specify a namefor your task. Optionally also add a description and
          assign it to user in Asana. Finally, click “Create Asana task”:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-create-task.png"
          alt="Create task view"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          5. A task will be created in the linked Asana account and a note is
          added to the Zendesk ticket with a link to the task. The note is not
          visible to the customer.
        </Typography>
        <Typography variant="body1" gutterBottom>
          The next time you visit the ticket you will also see a link to the
          task in the sidebar under “Linked tasks”:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-home-linked.png"
          alt="Linked project view"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          In the task description in Asana will be the description you entered
          plus the user who created it, information about the user and company
          associated with the ticket the task was created from:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-task-view.png"
          alt="Linked project view"
          className="image"
        />
        <Typography variant="body1" gutterBottom id="link-asana-task">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h4" gutterBottom>
          2. Link an existing Asana task
        </Typography>

        <Typography variant="body1" gutterBottom>
          This will link the Zendesk ticket you are looking at to an existing
          Asana task, post a link to the task in a note on the Zendesk ticket
          and a link to, and details of, the Zendesk ticket on the Asana task.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Additionally, any notes posted on the Asana task or changes in its
          status will be posted as notes on the Zendesk ticket. You will also be
          able to post comments on the Asana task from the Zendesk ticket.
        </Typography>
        <Typography variant="body1" gutterBottom>
          1. Select “Link task”:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-home-empty.png"
          alt="Linked project view"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          2. Search for a task by full or partial name e.g. “user creation bug”:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-link-task.png"
          alt="Linked project view"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          and select it from the results:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-select-task-to-link.png"
          alt="Linked project view"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          3. The task and ticket are now linked.
        </Typography>
        <Typography variant="body1" gutterBottom>
          The next time you visit the ticket the task name will be listed under
          “Linked tasks” in the Asana in Zendesk sidebar app.
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-home-linked-2.png"
          alt="Linked project view"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          Linked tasks are shown on the ticket in the right panel. You may need
          to refresh the ticket before it appears.
        </Typography>
        <Typography variant="body1" gutterBottom id="unlink-asana-task">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h4" gutterBottom>
          Unlinking a task
        </Typography>
        <Typography variant="body1" gutterBottom>
          The links between a Zendesk ticket and an Asana task can be removed if
          you no longer want updates on the Asana task to be posted to the
          Zendesk ticket.
        </Typography>
        <Typography variant="body1" gutterBottom>
          1. Go to the relevant ticket in Zendesk Support
        </Typography>
        <Typography variant="body1" gutterBottom>
          2. In the Asana in Zendesk sidebar app, find the task you want to
          unlink in the “Linked tasks” list and click it
        </Typography>
        <Typography variant="body1" gutterBottom>
          3. Click "Unlink" in the task view:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-task-home.png"
          alt="Task home"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          4. A confirmation screen is shown. You may need to refresh the Zendesk
          ticket for the linked task to disappear from the “Linked tasks” list.
        </Typography>
        <Typography variant="body1" gutterBottom id="comment-on-asana-task">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h4" gutterBottom>
          Commenting on a task
        </Typography>

        <Typography variant="body1" gutterBottom>
          You can post comments on an Asana task from the Zendesk ticket. This
          avoids your agents needing to access Asana to reply to questions on
          the Asana task or to add further information to the task.
        </Typography>
        <Typography variant="body1" gutterBottom>
          1. Go to the relevant ticket in Zendesk Support
        </Typography>
        <Typography variant="body1" gutterBottom>
          2. In the Asana in Zendesk sidebar app, find the task you want to
          comment on in the “Linked tasks” list and click it
        </Typography>
        <Typography variant="body1" gutterBottom>
          3. Click "Post comment" in the task view:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-post-comment.png"
          alt="Post comment"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          4. Enter your comment and click "Post comment"
        </Typography>
        <Typography variant="body1" gutterBottom>
          The comment will be posted to the Asana task including the details of
          the Zendesk user who posted it:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/asana/asana-comment-added.png"
          alt="Comment added to Asana task"
          className="image"
        />
        <Typography variant="body1" gutterBottom id="select-attributes">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h4" gutterBottom>
          Changing Asana user
        </Typography>
        <div className="border-left">
          <Typography variant="body1" gutterBottom>
            ℹ️ NOTE: Changing Asana user will unlink all existing tickets and
            tasks. Any Asana tasks created under the previous user will not post
            updates to Zendesk unless they are re-linked.
          </Typography>
        </div>
        <Typography variant="body1" gutterBottom>
          You may want to change the Asana user the app uses to create tasks to
          use a more generic user or one with different permissions. To do so:
        </Typography>
        <Typography variant="body1" gutterBottom>
          1. Ensure you are logged in to Asana with the user you want to use for
          the app
        </Typography>
        <Typography variant="body1" gutterBottom>
          2. In the Zendesk sidebar app, go to the “Settings” menu:
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-settings-button.png"
          alt="Settings button"
          className="image"
        />
        <Typography variant="body1" gutterBottom>
          3. In Settings, click “Reauthorize Asana access” and follow the
          prompts ensuring the correct Asana user and workspace are selected.
        </Typography>
        <img
          src="https://d2sztxeooqpvky.cloudfront.net/zendesk-asana/asana-reauthorize-access.png"
          alt="Reauthorize access"
          className="image"
        />
        <Typography variant="body1" gutterBottom id="changing-zendesk-user">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h4" gutterBottom>
          Changing Zendesk user
        </Typography>
        <Typography variant="body1" gutterBottom>
          You may want to change the Zendesk user the app uses to create tasks
          to use a more generic user, to be notified of new notes on tickets, or
          to use one with different permissions. To do so:
        </Typography>
        <Typography variant="body1" gutterBottom>
          1. While logged in as the Zendesk user that installed the app, go to
          the{" "}
          <a
            href="https://www.zendesk.com/marketplace/apps/support/1131851/asana-by-thackd/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Admin center, click "Apps and integrations" and "Zendesk Support
            apps"
          </a>
        </Typography>
        <Typography variant="body1" gutterBottom>
          2. Find the Asana by Thackd App and select it
        </Typography>
        <Typography variant="body1" gutterBottom>
          3. Click “Uninstall”
        </Typography>
        <Typography variant="body1" gutterBottom>
          4. Log in to Zendesk as the new user
        </Typography>
        <Typography variant="body1" gutterBottom>
          5. Go back to the Zendesk Marketplace and reinstall Asana in Zendesk
        </Typography>
        <Typography variant="body1" gutterBottom id="pricing">
          <a href="#top-of-page">Back to top</a>
        </Typography>

        <Typography variant="h3" gutterBottom>
          Pricing
        </Typography>
        <Typography variant="body1" gutterBottom>
          All features of Asana in Zendesk are currently free to use.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <a href="#top-of-page">Back to top</a>
        </Typography>
      </Styled>
    );
  };

  return (
    <Product
      productName={productName}
      productLogoUrl={productLogoUrl}
      Content={Content}
    />
  );
};

const Styled = styled.div`
  max-width: 800px;

  ul ul {
    padding-left: 20px;
  }

  ul p {
    margin-bottom: 0;
  }

  p {
    margin-bottom: 1.5rem;
  }

  h2 {
    margin-bottom: 2rem;
  }

  h3 {
    margin-top: 4rem;
    margin-bottom: 2rem;
  }

  h4 {
    margin-top: 4rem;
    margin-bottom: 2rem;
  }

  .light-text {
    font-style: italic;
    color: #999999;
  }

  .border-left {
    border-left: 1px solid #000;
    padding-left: 1rem;
  }

  .image {
    max-width: 600px;
    @media screen and (max-width: 600px) {
      max-width: 100%;
    }
    margin-bottom: 2rem;
  }

  .video {
    max-width: 600px;
    @media screen and (max-width: 600px) {
      max-width: 100%;
    }
    margin-bottom: 2rem;
  }
`;

export default AsanaInZendesk;
